import { Component, computed, effect, inject, signal } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { LoadWithView } from '../../../../core/models/load.model';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import {
  RadioCardGroupComponent,
  RadioOption,
} from '../../../../shared/ui/forms/radio-card-group/radio-card-group.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { NumberInputComponent } from '../../../../shared/ui/forms/number-input/number-input.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { PartnerSearchComponent } from '../../../../shared/ui/forms/partner-search/partner-search.component';
import { CompanySearchComponent } from '../../../../shared/ui/forms/company-search/company-search.component';
import { CompanyReference } from '../../../../core/services/company-search.service';
import { LoadService } from '../../../../core/services/load.service';
import { FleetService } from '../../../../core/services/fleet.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';
import { PartnerService } from '../../../../core/services/partner.service';
import { forkJoin } from 'rxjs';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';

import { AddPartnerModalComponent } from '../../../../shared/ui/add-partner-modal/add-partner-modal.component';

import { DatePickerComponent } from '../../../../shared/ui/forms/date-picker/date-picker.component';

@Component({
  selector: 'app-edit-assignments-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    RadioCardGroupComponent,
    FormCardComponent,
    InputComponent,
    NumberInputComponent,
    FormFieldComponent,
    CompanySearchComponent,
    PartnerSearchComponent,
    TextComponent,
    DatePickerComponent,

  ],
  templateUrl: './edit-assignments-form.component.html',
})
export class EditAssignmentsFormComponent {
  openAddPartnerModal(explicitType?: string) {
    const defaultType = explicitType || (this.partyType() === 'carrier' ? 'carrier' : (this.partyType() === 'customer' ? 'customer' : 'broker'));
    this.modalService.open(AddPartnerModalComponent, {
      title: 'Add Partner',
      subtitle: 'Search and add a partner to your network',
      icon: 'UserPlus',
      size: 'md',
      data: {
        defaultType,
        onSuccess: (newPartner?: any) => {
          if (newPartner) {
            if (explicitType === 'broker' && this.partyType() === 'carrier') {
              this.form.get('carrier.brokerCompany')?.setValue(newPartner);
            } else if (this.partyType() === 'broker') {
              this.form.get('broker.brokerCompany')?.setValue(newPartner);
            } else if (this.partyType() === 'customer') {
              this.form.get('customer.customerCompany')?.setValue(newPartner);
            } else if (this.partyType() === 'carrier') {
              this.form.get('carrier.carrierCompany')?.setValue(newPartner);
            }
          }
        }
      }
    });
  }
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly fleetService = inject(FleetService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly partnerService = inject(PartnerService);
  private readonly fb = inject(FormBuilder);

  protected readonly load: LoadWithView = this.modalService.state()!.data.load;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;

  // --- Card Options ---
  partyTypeOptions: RadioOption[] = [
    { value: 'customer', label: 'Direct Customer', icon: 'UserRound' },
    { value: 'broker', label: 'Freight Broker', icon: 'Briefcase' },
    { value: 'carrier', label: 'Forwarded / Carrier', icon: 'Truck' },
  ];
  haulingTypeOptions: RadioOption[] = [
    { value: 'self', label: 'My fleet', icon: 'Truck' },
    { value: 'forwarding', label: 'Forwarding', icon: 'Send' },
    { value: 'ownerOperator', label: 'Assigning to Owner-Op', icon: 'User' },
  ];

  // --- Signal-based State Management ---
  partyType = signal<string>('broker');
  haulingType = signal<string>('self');
  extractedBrokerName = signal<string>('');
  extractedCustomerName = signal<string>('');
  extractedCarrierName = signal<string>('');

  forwarded = computed(() => this.haulingType() !== 'self');

  constructor() {
    this.form = this.fb.group({
      partyType: ['broker', Validators.required],
      haulingType: ['self', Validators.required],
      bookedAt: [null],
      customer: this.fb.group({
        customerCompany: [null as CompanyReference | null],
        customerLoadId: [''],
        customerBookedAtRate: [null],
      }),
      broker: this.fb.group({
        brokerCompany: [null as CompanyReference | null],
        brokerLoadId: [''],
        brokerBookedAtRate: [null],
      }),
      carrier: this.fb.group({
        carrierCompany: [null as CompanyReference | null],
        carrierLoadId: [''],
        carrierBookedAtRate: [null],
        brokerCompany: [null as CompanyReference | null],
        brokerLoadId: [''],
      }),
      forwarding: this.fb.group({
        hauledByCompany: [null as CompanyReference | null],
        forwardRate: [null],
      }),
    });

    // Sync form controls to signals
    this.form
      .get('partyType')!
      .valueChanges.subscribe((value) => this.partyType.set(value));
    this.form
      .get('haulingType')!
      .valueChanges.subscribe((value) => this.haulingType.set(value));

    // Initialize form with data from the load
    this.initializeForm();

    // Effect to manage enabling/disabling form groups
    effect(() => this.updateFormState());
  }

  private initializeForm(): void {
    // partyType determination: customerId/brokerId/carrier now live in the view
    const view = this.load.view;
    let partyType = 'broker';
    if (view?.bookedFromRole === 'carrier' || (!view?.brokerId && !view?.customerId && (view?.bookedFromCompanyId || this.load.carrierOfRecordCompanyName))) {
      partyType = 'carrier';
    } else if (view?.customerId) {
      partyType = 'customer';
    } else if (this.load.dispatcherId) {
      partyType = 'dispatcher';
    }
    this.partyType.set(partyType);
    this.haulingType.set(
      this.load.hauledById === this.load.carrierId
        ? 'self'
        : this.load.forwardedToOwnerOp
          ? 'ownerOperator'
          : this.load.hauledById
            ? 'forwarding'
            : 'self'
    );

    // Build CompanyReference objects from the company view (financial data)
    // Only set as selected company if a valid linked ID exists. Otherwise, prompt user with extractedName helper.
    const brokerRef: any = view?.brokerId
      ? {
        id: view.brokerId,
        partnerCompanyId: view.brokerId,
        collection: 'companyDirectory',
        legalName: view.brokerName || '',
        partnerName: view.brokerName || '',
        dbaName: null,
        dotNumber: view.brokerDotNumber ?? undefined,
        mcNumber: view.brokerMcNumber ?? undefined,
        source: 'dot',
      }
      : null;

    if (!view?.brokerId && view?.brokerName) {
      this.extractedBrokerName.set(view.brokerName);
    }

    // Original broker reference (if load is forwarded from carrier or parent has original broker)
    const origBrokerId = this.load.originalBrokerCompanyId || (partyType === 'carrier' ? view?.brokerId : null);
    const origBrokerName = this.load.originalBrokerCompanyName || (partyType === 'carrier' ? view?.brokerName : null);
    const origBrokerDot = this.load.originalBrokerDotNumber ?? (partyType === 'carrier' ? view?.brokerDotNumber : undefined);
    const origBrokerMc = this.load.originalBrokerMcNumber ?? (partyType === 'carrier' ? view?.brokerMcNumber : undefined);
    const origBrokerLoadNum = this.load.originalBrokerLoadNumber || '';

    const origBrokerRef: any = origBrokerId
      ? {
        id: origBrokerId,
        partnerCompanyId: origBrokerId,
        collection: 'companyDirectory',
        legalName: origBrokerName || '',
        partnerName: origBrokerName || '',
        dbaName: null,
        dotNumber: origBrokerDot ?? undefined,
        mcNumber: origBrokerMc ?? undefined,
        source: 'dot',
      }
      : null;

    if (!origBrokerId && origBrokerName) {
      this.extractedBrokerName.set(origBrokerName);
    }

    const customerRef: any = view?.customerId
      ? {
        id: view.customerId,
        partnerCompanyId: view.customerId,
        collection: 'companyDirectory',
        legalName: view.customerName || '',
        partnerName: view.customerName || '',
        dbaName: null,
        dotNumber: view.customerDotNumber ?? undefined,
        mcNumber: view.customerMcNumber ?? undefined,
        source: 'dot',
      }
      : null;

    if (!view?.customerId && view?.customerName) {
      this.extractedCustomerName.set(view.customerName);
    }

    const carrierName = view?.bookedFromCompanyName || (partyType === 'carrier' ? (this.load.carrierOfRecordCompanyName || this.load.carrierName) : null);
    const carrierId = view?.bookedFromCompanyId || (partyType === 'carrier' ? (this.load.carrierOfRecordCompanyId || this.load.carrierId) : null);

    const carrierRef: any = carrierId
      ? {
        id: carrierId,
        partnerCompanyId: carrierId,
        collection: 'companyDirectory',
        legalName: carrierName || '',
        partnerName: carrierName || '',
        dbaName: null,
        dotNumber: view?.bookedFromDotNumber ?? this.load.carrierOfRecordDotNumber ?? this.load.carrierDotNumber ?? undefined,
        mcNumber: view?.bookedFromMcNumber ?? undefined,
        source: 'dot',
      }
      : null;

    if (!carrierId && carrierName) {
      this.extractedCarrierName.set(carrierName);
    }

    const hauledByRef: any = this.load.hauledById &&
      this.load.hauledById !== this.load.carrierId
      ? {
        id: this.load.hauledById,
        collection: 'companyDirectory',
        legalName: this.load.hauledByName || '',
        partnerName: this.load.hauledByName || '',
        dotNumber: this.load.hauledByDotNumber,
        source: 'dot',
      }
      : null;

    this.form.patchValue(
      {
        partyType: partyType,
        bookedAt: this.load.view?.pricing?.bookedAt ? ((this.load.view.pricing.bookedAt as any).toDate().toISOString().substring(0, 10)) : null,
        customer: {
          customerCompany: customerRef,
          customerLoadId: this.load.externalId,
          customerBookedAtRate: this.load.view?.pricing?.baseRate || null,
        },
        broker: {
          brokerCompany: brokerRef,
          brokerLoadId: this.load.externalId,
          brokerBookedAtRate: this.load.view?.pricing?.baseRate || null,
        },
        carrier: {
          carrierCompany: carrierRef,
          carrierLoadId: this.load.externalId,
          carrierBookedAtRate: this.load.view?.pricing?.baseRate || null,
          brokerCompany: origBrokerRef,
          brokerLoadId: origBrokerLoadNum,
        },
        haulingType:
          this.load.hauledById === this.load.carrierId
            ? 'self'
            : this.load.forwardedToOwnerOp
              ? 'ownerOperator'
              : this.load.hauledById
                ? 'forwarding'
                : 'self',
        forwarding: {
          hauledByCompany: hauledByRef,
          forwardRate: this.load.view?.pricing?.baseRate || null,
        },
      },
      { emitEvent: false }
    );
  }

  private updateFormState(): void {
    const party = this.partyType();
    const hauling = this.haulingType();

    ['customer', 'broker', 'carrier'].forEach((group) => {
      const control = this.form.get(group);
      if (party === group) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    });

    const forwardingControl = this.form.get('forwarding');
    if (hauling !== 'self') {
      forwardingControl?.enable({ emitEvent: false });
    } else {
      forwardingControl?.disable({ emitEvent: false });
    }
  }

  cancel(): void {
    this.modalService.close();
  }

  /**
   * Resolves the canonical global company / companyDirectory ID rather than a
   * company-scoped `companyRelationships` composite ID (`{ownerCompanyId}_{partnerCompanyId}`).
   */
  private resolveCanonicalCompanyId(ref: any): string | null {
    if (!ref) return null;
    const raw = (ref.partnerCompanyId || ref.id || '').trim();
    if (!raw) return null;
    const activeCid = this.fleetService.currentCompanySignal()?.id;
    if (activeCid && raw.startsWith(`${activeCid}_`)) {
      return raw.slice(activeCid.length + 1) || raw;
    }
    return raw;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Set local saving state
    this.isSaving.set(true);

    // Show global loading overlay
    this.loadingService.show({
      variant: 'overlay',
      title: 'Updating Booking Details',
      subtitle: 'Saving your changes...',
    });

    // Map form value to a partial Load object for updating
    const formValue = this.form.getRawValue();

    // Map party fields
    // Financial fields ─ go to views/{companyId}
    const viewData: Record<string, any> = {};
    // Operational fields ─ go to parent loads/{id}
    const updatedData: Record<string, any> = {};

    if (this.partyType() === 'carrier') {
      const carrierCompany: any = formValue.carrier.carrierCompany;
      const carrierName = carrierCompany?.dbaName || carrierCompany?.partnerName || carrierCompany?.legalName || null;
      const carrierCompanyId = this.resolveCanonicalCompanyId(carrierCompany);
      const carrierDot = carrierCompany?.dotNumber ? String(carrierCompany.dotNumber) : null;
      const carrierMc = carrierCompany?.mcNumber ? String(carrierCompany.mcNumber) : null;

      // Financial → view
      viewData['bookedFromCompanyId'] = carrierCompanyId;
      viewData['bookedFromCompanyName'] = carrierName;
      viewData['bookedFromDotNumber'] = carrierDot;
      viewData['bookedFromMcNumber'] = carrierMc;
      viewData['bookedFromRole'] = 'carrier';
      viewData['pricing'] = {
        ...this.load.view?.pricing,
        baseRate: formValue.carrier.carrierBookedAtRate || null,
        bookedAt: formValue.bookedAt ? new Date(formValue.bookedAt) : null,
      };

      // Auto-create partner if needed (e.g. from general company directory)
      const partnerName = carrierCompany?.partnerName || carrierCompany?.legalName;
      if (partnerName && carrierCompany.collection !== 'companyRelationships' && !carrierCompany.partnerTypes) {
        this.partnerService.ensurePartnerExists({
          partnerCompanyId: carrierCompanyId || undefined,
          partnerName: partnerName,
          dotNumber: carrierDot || undefined,
          mcNumber: carrierMc || undefined,
          partnerType: 'carrier',
        }).catch(err => console.error('Failed to ensure carrier partner exists:', err));
      }

      // Clear broker & customer in view
      viewData['brokerId'] = null;
      viewData['brokerName'] = null;
      viewData['brokerDotNumber'] = null;
      viewData['brokerMcNumber'] = null;
      viewData['customerId'] = null;
      viewData['customerName'] = null;
      viewData['customerDotNumber'] = null;
      viewData['customerMcNumber'] = null;

      // Carrier of Record on parent load
      updatedData['carrierOfRecordCompanyId'] = carrierCompanyId;
      updatedData['carrierOfRecordCompanyName'] = carrierName;
      updatedData['carrierOfRecordDotNumber'] = carrierDot;
      updatedData['carrierId'] = carrierCompanyId;
      updatedData['carrierName'] = carrierName;
      updatedData['carrierDotNumber'] = carrierDot;
      updatedData['externalId'] = formValue.carrier.carrierLoadId || null;

      // Optional Original Broker Details
      const origBrokerCompany: any = formValue.carrier.brokerCompany;
      if (origBrokerCompany) {
        const origBrokerName = origBrokerCompany?.dbaName || origBrokerCompany?.partnerName || origBrokerCompany?.legalName || null;
        const origBrokerId = this.resolveCanonicalCompanyId(origBrokerCompany);
        const origBrokerDot = origBrokerCompany?.dotNumber ? String(origBrokerCompany.dotNumber) : null;
        const origBrokerMc = origBrokerCompany?.mcNumber ? String(origBrokerCompany.mcNumber) : null;

        updatedData['originalBrokerCompanyId'] = origBrokerId;
        updatedData['originalBrokerCompanyName'] = origBrokerName;
        updatedData['originalBrokerDotNumber'] = origBrokerDot;
        updatedData['originalBrokerMcNumber'] = origBrokerMc;
        updatedData['originalBrokerLoadNumber'] = formValue.carrier.brokerLoadId || null;

        // Auto-create partner if needed (e.g. from general company directory)
        const brokerPartnerName = origBrokerCompany?.partnerName || origBrokerCompany?.legalName;
        if (brokerPartnerName && origBrokerCompany.collection !== 'companyRelationships' && !origBrokerCompany.partnerTypes) {
          this.partnerService.ensurePartnerExists({
            partnerCompanyId: origBrokerId || undefined,
            partnerName: brokerPartnerName,
            dotNumber: origBrokerDot || undefined,
            mcNumber: origBrokerMc || undefined,
            partnerType: 'broker',
          }).catch(err => console.error('Failed to ensure original broker partner exists:', err));
        }
      } else {
        updatedData['originalBrokerCompanyId'] = null;
        updatedData['originalBrokerCompanyName'] = null;
        updatedData['originalBrokerDotNumber'] = null;
        updatedData['originalBrokerMcNumber'] = null;
        updatedData['originalBrokerLoadNumber'] = formValue.carrier.brokerLoadId || null;
      }
    } else if (this.partyType() === 'broker') {
      const brokerCompany: any = formValue.broker.brokerCompany;
      const brokerName = brokerCompany?.dbaName || brokerCompany?.partnerName || brokerCompany?.legalName || null;
      const canonicalBrokerId = this.resolveCanonicalCompanyId(brokerCompany);
      // Financial → view
      viewData['bookedFromCompanyId'] = canonicalBrokerId;
      viewData['bookedFromCompanyName'] = brokerName;
      viewData['bookedFromDotNumber'] = brokerCompany?.dotNumber || null;
      viewData['bookedFromMcNumber'] = brokerCompany?.mcNumber || null;
      viewData['bookedFromRole'] = 'broker';

      viewData['brokerId'] = canonicalBrokerId;
      viewData['brokerName'] = brokerName;
      viewData['brokerDotNumber'] = brokerCompany?.dotNumber || null;
      viewData['brokerMcNumber'] = brokerCompany?.mcNumber || null;
      viewData['pricing'] = { 
        ...this.load.view?.pricing,
        baseRate: formValue.broker.brokerBookedAtRate || null,
        bookedAt: formValue.bookedAt ? new Date(formValue.bookedAt) : null
      };
      
      // Auto-create partner if needed (e.g. from general company directory)
      const partnerName = brokerCompany?.partnerName || brokerCompany?.legalName;
      if (partnerName && brokerCompany.collection !== 'companyRelationships' && !brokerCompany.partnerTypes) {
        this.partnerService.ensurePartnerExists({
          partnerCompanyId: canonicalBrokerId || undefined,
          partnerName: partnerName,
          dotNumber: brokerCompany.dotNumber,
          mcNumber: brokerCompany.mcNumber,
          partnerType: 'broker'
        }).catch(err => console.error('Failed to ensure partner exists:', err));
      }

      // Clear customer in view
      viewData['customerId'] = null;
      viewData['customerName'] = null;
      viewData['customerDotNumber'] = null;
      viewData['customerMcNumber'] = null;
      // externalId stays on parent (operational reference)
      updatedData['externalId'] = formValue.broker.brokerLoadId || null;
      updatedData['originalBrokerCompanyId'] = canonicalBrokerId;
      updatedData['originalBrokerCompanyName'] = brokerName;
      updatedData['originalBrokerDotNumber'] = brokerCompany?.dotNumber || null;
      updatedData['originalBrokerMcNumber'] = brokerCompany?.mcNumber || null;
      updatedData['originalBrokerLoadNumber'] = formValue.broker.brokerLoadId || null;
    } else {
      const customerCompany: any = formValue.customer.customerCompany;
      const customerName = customerCompany?.dbaName || customerCompany?.partnerName || customerCompany?.legalName || null;
      const canonicalCustomerId = this.resolveCanonicalCompanyId(customerCompany);
      // Financial → view
      viewData['bookedFromCompanyId'] = canonicalCustomerId;
      viewData['bookedFromCompanyName'] = customerName;
      viewData['bookedFromDotNumber'] = customerCompany?.dotNumber || null;
      viewData['bookedFromMcNumber'] = customerCompany?.mcNumber || null;
      viewData['bookedFromRole'] = 'direct_shipper';

      viewData['customerId'] = canonicalCustomerId;
      viewData['customerName'] = customerName;
      viewData['customerDotNumber'] = customerCompany?.dotNumber || null;
      viewData['customerMcNumber'] = customerCompany?.mcNumber || null;
      viewData['pricing'] = { 
        ...this.load.view?.pricing,
        baseRate: formValue.customer.customerBookedAtRate || null,
        bookedAt: formValue.bookedAt ? new Date(formValue.bookedAt) : null
      };
      
      // Auto-create partner if needed
      const partnerName = customerCompany?.partnerName || customerCompany?.legalName;
      if (partnerName && customerCompany.collection !== 'companyRelationships' && !customerCompany.partnerTypes) {
        this.partnerService.ensurePartnerExists({
          partnerCompanyId: canonicalCustomerId || undefined,
          partnerName: partnerName,
          dotNumber: customerCompany.dotNumber,
          mcNumber: customerCompany.mcNumber,
          partnerType: 'customer'
        }).catch(err => console.error('Failed to ensure partner exists:', err));
      }

      // Clear broker in view
      viewData['brokerId'] = null;
      viewData['brokerName'] = null;
      viewData['brokerDotNumber'] = null;
      viewData['brokerMcNumber'] = null;
      updatedData['externalId'] = formValue.customer.customerLoadId || null;
      updatedData['originalBrokerCompanyId'] = null;
      updatedData['originalBrokerCompanyName'] = null;
      updatedData['originalBrokerDotNumber'] = null;
      updatedData['originalBrokerMcNumber'] = null;
      updatedData['originalBrokerLoadNumber'] = null;
    }

    // Map hauling fields — all operational (who moves the load)
    const currentCompany = this.fleetService.currentCompanySignal();
    const myCompanyId = currentCompany?.id ?? null;
    const myCompanyName = currentCompany?.dbaName ?? currentCompany?.legalName ?? null;
    const myCompanyDot = currentCompany?.dotNumber ?? null;

    if (this.haulingType() === 'self') {
      if (this.partyType() === 'carrier') {
        // Forwarded from another carrier:
        // Carrier of record is the forwarding carrier (already set in carrierOfRecord* above).
        // Hauled By and Operated By is our company.
        updatedData['hauledById'] = myCompanyId;
        updatedData['hauledByName'] = myCompanyName;
        updatedData['hauledByDotNumber'] = myCompanyDot;
        updatedData['operatedByCompanyId'] = myCompanyId;
        updatedData['operatedByCompanyName'] = myCompanyName;
        updatedData['operatedByDotNumber'] = myCompanyDot;
        updatedData['operatedByRole'] = 'carrier';
        updatedData['forwardedToOwnerOp'] = false;
        viewData['forwardRate'] = null;
      } else {
        const effectiveCarrierId = this.load.carrierId ?? myCompanyId;
        const effectiveCarrierName = this.load.carrierName ?? myCompanyName;
        const effectiveCarrierDot = this.load.carrierDotNumber ?? myCompanyDot;
        updatedData['carrierId'] = effectiveCarrierId;
        updatedData['carrierName'] = effectiveCarrierName;
        updatedData['carrierDotNumber'] = effectiveCarrierDot;
        updatedData['carrierOfRecordCompanyId'] = effectiveCarrierId;
        updatedData['carrierOfRecordCompanyName'] = effectiveCarrierName;
        updatedData['carrierOfRecordDotNumber'] = effectiveCarrierDot;

        updatedData['hauledById'] = effectiveCarrierId;
        updatedData['hauledByName'] = effectiveCarrierName;
        updatedData['hauledByDotNumber'] = effectiveCarrierDot;
        updatedData['operatedByCompanyId'] = effectiveCarrierId;
        updatedData['operatedByCompanyName'] = effectiveCarrierName;
        updatedData['operatedByDotNumber'] = effectiveCarrierDot;
        updatedData['operatedByRole'] = 'carrier';
        updatedData['forwardedToOwnerOp'] = false;
        viewData['forwardRate'] = null;
      }
    } else {
      const hauledByCompany: any = formValue.forwarding.hauledByCompany;
      const opName = hauledByCompany?.dbaName || hauledByCompany?.legalName || hauledByCompany?.partnerName || null;
      const isOwnerOp = formValue.haulingType === 'ownerOperator';
      updatedData['hauledById'] = hauledByCompany?.id || null;
      updatedData['hauledByName'] = opName;
      updatedData['hauledByDotNumber'] = hauledByCompany?.dotNumber || null;
      updatedData['operatedByCompanyId'] = hauledByCompany?.id || null;
      updatedData['operatedByCompanyName'] = opName;
      updatedData['operatedByDotNumber'] = hauledByCompany?.dotNumber || null;
      updatedData['operatedByRole'] = isOwnerOp ? 'owner_operator' : 'carrier';
      updatedData['forwardedToOwnerOp'] = isOwnerOp;
      viewData['forwardRate'] = formValue.forwarding.forwardRate || null;
    }

    // Determine companyId for updating the view (our company's view document)
    const viewCompanyId = myCompanyId || this.load.carrierId || this.load.participantCompanyIds?.[0];
    if (!viewCompanyId) {
      this.loadingService.hide();
      this.toastService.show('error', 'Cannot determine company for this load.', { title: 'Error', duration: 5000 });
      this.isSaving.set(false);
      return;
    }

    // Write operational fields to parent, financial fields to view — in parallel
    const parent$ = this.loadService.updateLoad(this.load.id, updatedData);
    const view$ = this.loadService.updateCompanyView(this.load.id, viewCompanyId, viewData);

    forkJoin([parent$, view$]).subscribe({
      next: () => {
        this.loadingService.hide();
        this.modalService.close();
        this.onSuccess?.();
        setTimeout(() => {
          this.toastService.show(
            'success',
            'Booking details updated successfully',
            { title: 'Success', duration: 4000 }
          );
        }, 100);
      },
      error: () => {
        this.loadingService.hide();
        this.toastService.show(
          'error',
          'Failed to update booking details. Please try again.',
          { title: 'Update Failed', showClose: true, duration: 0 }
        );
      },
      complete: () => {
        this.isSaving.set(false);
      }
    });
  }
}
